import { CheckSquareOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  DatePicker,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { getCompanies } from 'api/companies';
import { fetchAllCustomers } from 'api/customers';
import { getTaskAssignees, getTasks, updateTask } from 'api/tasks';
import Layout from 'components/Layout';
import { PERMISSIONS } from 'constants/permissions';
import AddTaskDrawer from 'containers/CustomerDetails/ActivityTab/AddTaskDrawer';
import dayjs from 'dayjs';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'done', label: 'Done' },
];

function displayName(user) {
  if (!user) return 'Unassigned';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || 'Unknown';
}

const CELL_TEXT_STYLE = { display: 'block', maxWidth: '100%' };
const CELL_SUBTEXT_STYLE = { display: 'block', maxWidth: '100%', fontSize: 12 };

function RelatedEntity({ hasCrmAccess, label, name, to }) {
  const displayValue = name || '—';
  const nameText = (
    <Text ellipsis={{ tooltip: displayValue }} style={CELL_TEXT_STYLE}>
      {displayValue}
    </Text>
  );
  return (
    <div>
      {hasCrmAccess ? <Link to={to}>{nameText}</Link> : nameText}
      <Text type="secondary" style={CELL_SUBTEXT_STYLE}>{label}</Text>
    </div>
  );
}

function unwrapList(res) {
  const data = getData(res);
  return {
    results: data?.data?.results ?? data?.results ?? [],
    count: data?.data?.count ?? data?.count ?? 0,
  };
}

function Tasks() {
  const { message } = App.useApp();
  const hasCrmAccess = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);
  const [assignee, setAssignee] = useState('me');
  const [status, setStatus] = useState('');
  const [dueRange, setDueRange] = useState(null);
  const [companyId, setCompanyId] = useState();
  const [customerId, setCustomerId] = useState();
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const fetchTasks = useCallback(() => {
    setLoading(true);
    const params = {
      assignee: hasCrmAccess ? assignee : 'me',
      page,
      page_size: pageSize,
    };
    if (status) params.status = status;
    if (hasCrmAccess && companyId) params.company = companyId;
    if (hasCrmAccess && customerId) params.customer = customerId;
    if (dueRange?.[0]) params.due_after = dueRange[0].startOf('day').toISOString();
    if (dueRange?.[1]) params.due_before = dueRange[1].endOf('day').toISOString();

    getTasks(params)
      .then((res) => {
        const { results, count } = unwrapList(res);
        setTasks(Array.isArray(results) ? results : []);
        setTotal(count);
      })
      .catch((error) => {
        captureException(error);
        message.error(getHttpErrorMessage(error) || 'Failed to load tasks');
      })
      .finally(() => setLoading(false));
  }, [assignee, companyId, customerId, dueRange, hasCrmAccess, message, page, pageSize, status]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!hasCrmAccess) return;

    getCompanies({ page: 1, page_size: 100 })
      .then((res) => {
        const { results } = unwrapList(res);
        setCompanies(Array.isArray(results) ? results : []);
      })
      .catch((error) => captureException(error));

    fetchAllCustomers({ page: 1, page_size: 100 })
      .then((res) => {
        const { results } = unwrapList(res);
        setCustomers(Array.isArray(results) ? results : []);
      })
      .catch((error) => captureException(error));

    getTaskAssignees()
      .then((res) => {
        const data = getData(res);
        const users = data?.data ?? data ?? [];
        setAssignees(Array.isArray(users) ? users : []);
      })
      .catch((error) => captureException(error));
  }, [hasCrmAccess]);

  const handleStatusChange = (task, nextStatus) => {
    setUpdatingId(task.id);
    updateTask(task.id, { status: nextStatus })
      .then(() => {
        message.success(nextStatus === 'done' ? 'Task marked done' : 'Task reopened');
        fetchTasks();
      })
      .catch((error) => {
        captureException(error);
        message.error(getHttpErrorMessage(error) || 'Failed to update task');
      })
      .finally(() => setUpdatingId(null));
  };

  const handleEditSubmit = (data) => updateTask(editingTask.id, data).then(() => {
    setEditingTask(null);
    message.success('Task updated');
    fetchTasks();
  });

  const companyOptions = useMemo(
    () => companies.map((company) => ({ value: company.id, label: company.name })),
    [companies],
  );

  const customerOptions = useMemo(
    () => customers.map((customer) => ({
      value: customer.id,
      label: customer.name || customer.email || `Customer ${customer.id}`,
    })),
    [customers],
  );

  const assigneeOptions = useMemo(() => {
    const people = assignees.map((user) => ({
      value: String(user.id),
      label: displayName(user),
    }));
    return [
      { value: 'me', label: 'My Tasks' },
      { value: 'all', label: 'All Tasks' },
      ...people,
    ];
  }, [assignees]);

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title) => (
        <Text ellipsis={{ tooltip: title }} style={CELL_TEXT_STYLE}>{title}</Text>
      ),
    },
    {
      title: 'Related',
      key: 'related',
      width: 160,
      render: (_, record) => {
        const links = [];
        if (record.customer?.id) {
          links.push(
            <RelatedEntity
              key={`c-${record.customer.id}`}
              hasCrmAccess={hasCrmAccess}
              label="Customer"
              name={record.customer.name}
              to={`/console/customers/${record.customer.id}/activity`}
            />,
          );
        }
        if (record.company?.id) {
          links.push(
            <RelatedEntity
              key={`co-${record.company.id}`}
              hasCrmAccess={hasCrmAccess}
              label="Company"
              name={record.company.name}
              to={`/console/customers/companies/${record.company.id}/activity`}
            />,
          );
        }
        if (!links.length) return '—';
        return <Space direction="vertical" size={8} style={{ width: '100%' }}>{links}</Space>;
      },
    },
    {
      title: 'Assignee',
      key: 'assigned_to',
      width: 160,
      render: (_, record) => {
        const name = displayName(record.assigned_to);
        return (
          <Text ellipsis={{ tooltip: name }} style={CELL_TEXT_STYLE}>{name}</Text>
        );
      },
    },
    {
      title: 'Due',
      dataIndex: 'due_at',
      key: 'due_at',
      width: 130,
      render: (dueAt, record) => {
        if (!dueAt) return '—';
        const overdue = record.status === 'pending' && dayjs(dueAt).isBefore(dayjs());
        const due = dayjs(dueAt);
        return (
          <div>
            <Text type={overdue ? 'danger' : undefined} style={CELL_TEXT_STYLE}>
              {due.format('MMM D, YYYY')}
            </Text>
            <Text type={overdue ? 'danger' : 'secondary'} style={CELL_SUBTEXT_STYLE}>
              {due.format('h:mm A')}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value) => (
        <Tag color={value === 'done' ? 'success' : 'processing'}>
          {value === 'done' ? 'Done' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          {hasCrmAccess ? (
            <Button size="small" onClick={() => setEditingTask(record)}>
              Edit
            </Button>
          ) : null}
          {record.status === 'done' ? (
            <Button
              size="small"
              loading={updatingId === record.id}
              onClick={() => handleStatusChange(record, 'pending')}
            >
              Reopen
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              loading={updatingId === record.id}
              onClick={() => handleStatusChange(record, 'done')}
            >
              Mark done
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout
      header={(
        <Layout.Header
          title="Tasks"
          description="Work follow-ups created from customer and company activity."
        />
      )}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        {hasCrmAccess ? (
          <Select
            style={{ width: 200 }}
            value={assignee}
            options={assigneeOptions}
            onChange={(value) => {
              setPage(1);
              setAssignee(value);
            }}
          />
        ) : null}
        <Select
          style={{ width: 160 }}
          value={status}
          options={STATUS_OPTIONS}
          onChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
        />
        <RangePicker
          value={dueRange}
          onChange={(value) => {
            setPage(1);
            setDueRange(value);
          }}
        />
        {hasCrmAccess ? (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filter by company"
            style={{ width: 220 }}
            value={companyId}
            options={companyOptions}
            onChange={(value) => {
              setPage(1);
              setCompanyId(value);
            }}
          />
        ) : null}
        {hasCrmAccess ? (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filter by customer"
            style={{ width: 220 }}
            value={customerId}
            options={customerOptions}
            onChange={(value) => {
              setPage(1);
              setCustomerId(value);
            }}
          />
        ) : null}
      </Space>
      {tasks.length || loading ? (
        <Table
          rowKey="id"
          loading={loading}
          tableLayout="fixed"
          columns={columns}
          dataSource={tasks}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      ) : (
        <Empty
          image={<CheckSquareOutlined style={{ fontSize: 48, opacity: 0.25 }} />}
          description={hasCrmAccess
            ? 'No tasks yet. Create one from a customer or company Activity tab.'
            : 'You have no tasks assigned to you in this organization.'}
        />
      )}
      <AddTaskDrawer
        open={Boolean(editingTask)}
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onSubmit={handleEditSubmit}
      />
    </Layout>
  );
}

export default Tasks;
